import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseDraftAutosaveOptions<T> {
  debounceMs?: number;
  save: (payload: Partial<T>) => Promise<void>;
  onError?: (error: unknown) => void;
}

export function useDraftAutosave<T>({
  debounceMs = 800,
  save,
  onError,
}: UseDraftAutosaveOptions<T>) {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const sequenceRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Partial<T> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const payload = pendingRef.current;
    if (!payload || Object.keys(payload).length === 0) return;

    pendingRef.current = null;
    const seq = ++sequenceRef.current;
    setStatus('saving');

    try {
      await saveRef.current(payload);
      if (seq === sequenceRef.current) {
        setStatus('saved');
      }
    } catch (error) {
      if (seq === sequenceRef.current) {
        setStatus('error');
        onError?.(error);
      }
    }
  }, [onError]);

  const queueSave = useCallback(
    (payload: Partial<T>, immediate = false) => {
      pendingRef.current = { ...pendingRef.current, ...payload };

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (immediate) {
        void flush();
        return;
      }

      timerRef.current = setTimeout(() => {
        void flush();
      }, debounceMs);
    },
    [debounceMs, flush]
  );

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingRef.current) {
        void flush();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [flush]);

  return { status, setStatus, queueSave, flush };
}
